import React, { useState } from 'react';
import { Button, toast, Total, Icon, MultiStepper, H2 } from '@tviklax/ui-components';
import { useGraphQLMutation } from '@tviklax/ui-components';
import { OrderFormData, OrderItem, RemittanceTrancheFormData } from './types';
import { CustomerInformation } from './CustomerInformation';
import { VendorInformation } from './VendorInformation';
import { ShippingInformation } from './ShippingInformation';
import { ItemsSection } from './ItemsSection';
import { DeliverySchedule } from './DeliverySchedule';
import { ImportInvoice } from './ImportInvoice';
import { ImportInvoiceReview } from './ImportInvoiceReview';
import { ReviewExtractedData } from './ReviewExtractedData';
import { ShippingSummary } from './ShippingSummary';
import { BulkUpload } from './BulkUpload';
import { ErpIntegration } from './ErpIntegration';
import { API_BASE_URL } from '../../../shared/constants/env';
import {
  CREATE_ORDER_MUTATION,
  CreateOrderData,
  CreateOrderVariables
} from '../graphql/createOrder.mutation';
import {
  CreateOrderContainer,
  PageHeaderCard,
  HeaderContent,
  IconWrapper,
  TitleGroup,
  Title,
  Subtitle,
  FormCard,
  TotalsGrid,
  MethodSelectionGroup
} from './styles';

const initialFormData: OrderFormData = {
  CustomerName: '',
  CustomerEmail: '',
  CustomerContactNumber: '',
  customerAddr1: '',
  customerAddr2: '',
  customerCity: '',
  customerState: '',
  customerCountry: '',
  customerZip: '',
  VendorName: '',
  VendorEmail: '',
  VendorContactNumber: '',
  vendorAddr1: '',
  vendorAddr2: '',
  vendorCity: '',
  vendorState: '',
  vendorCountry: '',
  vendorZip: '',
  SalesPerson: '',
  pickupAddr1: '',
  pickupAddr2: '',
  pickupCity: '',
  pickupState: '',
  pickupCountry: '',
  pickupZip: '',
  deliveryAddr1: '',
  deliveryAddr2: '',
  deliveryCity: '',
  deliveryState: '',
  deliveryCountry: '',
  deliveryZip: '',
  packageType: '',
  weight: '',
  dimensions: '',
  deliveryDate: '',
  remittanceTranches: [
    {
      percentage: 100,
      dueDate: '',
      milestone: 'Full remittance',
    },
  ],
  specialInstructions: '',
  items: [],
  subtotal: 0,
  taxPercent: 5,
  taxAmount: 0,
  discountPercent: 0,
  discountAmount: 0,
  total: 0,
  currency: 'USD',
  insuranceRequired: false,
  customsDocumentsRequired: false,
  priorityLevel: 'Normal',
  vendorSameAsPickup: true,
  deliverySameAsCustomer: true,
};

const initialNewItem: OrderItem = {
  itemno: '',
  productId: '',
  productName: '',
  details: '',
  unit: 'pcs',
  quantity: 0,
  unitPrice: 0,
  price: 0,
  total: 0,
  subtotal: 0,
  countryOfOrigin: '',
  exportControlFlag: '',
  ecgcEligibility: 'Pending check',
};

type CreationMode = 'import' | 'bulk' | 'workflow' | 'erp';

export const CreateOrder: React.FC = () => {
  const [creationMode, setCreationMode] = useState<CreationMode>('import');
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<OrderFormData>(initialFormData);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [extractedData, setExtractedData] = useState<any>(null);
  const [extractedFile, setExtractedFile] = useState<File | null>(null);
  const [insuranceSelections, setInsuranceSelections] = useState({
    ecgc: { applied: false, coverageType: 'shipment' as 'shipment' | 'buyer', notes: '' },
    cargo: { applied: false, coverageType: 'shipment' as 'shipment' | 'buyer', notes: '' },
  });
  const [contactValidity, setContactValidity] = useState({
    customerEmail: true,
    customerPhone: true,
    vendorEmail: true,
    vendorPhone: true,
  });

  const [createOrder] = useGraphQLMutation<CreateOrderData, CreateOrderVariables>(
    CREATE_ORDER_MUTATION
  );

  const buildRemittanceSummary = (tranches: RemittanceTrancheFormData[]) => {
    return tranches
      .filter(tranche => tranche.percentage > 0)
      .map(tranche => `${tranche.percentage}% ${tranche.milestone || 'payment'}`)
      .join('; ')
      .slice(0, 100);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const finalValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setFormData((prev) => ({ ...prev, [name]: finalValue }));
  };

  const handleAddressAutofill = (fields: Partial<OrderFormData>) => {
    setFormData((prev) => ({ ...prev, ...fields }));
  };

  const handleRemittanceTrancheChange = (
    index: number,
    field: keyof RemittanceTrancheFormData,
    value: string | number,
  ) => {
    setFormData(prev => ({
      ...prev,
      remittanceTranches: prev.remittanceTranches.map((tranche, trancheIndex) =>
        trancheIndex === index
          ? {
              ...tranche,
              [field]: field === 'percentage' ? Math.max(0, Number(value) || 0) : value,
            }
          : tranche,
      ),
    }));
  };

  const handleAddRemittanceTranche = () => {
    setFormData(prev => ({
      ...prev,
      remittanceTranches: [
        ...prev.remittanceTranches,
        {
          percentage: 0,
          dueDate: '',
          milestone: '',
        },
      ],
    }));
  };

  const handleRemoveRemittanceTranche = (index: number) => {
    setFormData(prev => ({
      ...prev,
      remittanceTranches:
        prev.remittanceTranches.length === 1
          ? prev.remittanceTranches
          : prev.remittanceTranches.filter((_, trancheIndex) => trancheIndex !== index),
    }));
  };

  const recomputeFinancials = (items: OrderItem[], base: OrderFormData) => {
    const normalized = items.map((item, idx) => {
      const qty = Number(item.quantity) || 0;
      const unitPrice = Number(item.price ?? item.unitPrice) || 0;
      const lineTotal = Number((qty * unitPrice).toFixed(2));
      return {
        ...item,
        itemno: idx + 1,
        quantity: qty,
        unitPrice,
        price: unitPrice,
        total: lineTotal,
        subtotal: lineTotal,
      };
    });

    const subtotal = normalized.reduce((sum, item) => sum + (item.total || 0), 0);
    const taxAmount = Number(((subtotal * (base.taxPercent || 0)) / 100).toFixed(2));
    const discountAmount = Number(((subtotal * (base.discountPercent || 0)) / 100).toFixed(2));
    const total = Number((subtotal - discountAmount + taxAmount).toFixed(2));

    return { items: normalized, subtotal, taxAmount, discountAmount, total };
  };

  const updateItems = (updater: (prevItems: OrderItem[]) => OrderItem[]) => {
    setFormData((prev) => {
      const nextItems = updater(prev.items);
      const { items, subtotal, taxAmount, discountAmount, total } = recomputeFinancials(nextItems, prev);
      return { ...prev, items, subtotal, taxAmount, discountAmount, total };
    });
  };

  const handleDataExtracted = (data: any, file: File) => {
    setExtractedData(data);
    setExtractedFile(file);
    setCreationMode('import');
  };

  const handleConfirmImport = (overrideData?: Partial<OrderFormData>) => {
    const sourceData = overrideData || extractedData;
    setFormData(prev => {
      const merged = { ...prev, ...sourceData } as OrderFormData;
      const { items, subtotal, taxAmount, discountAmount, total } = recomputeFinancials(merged.items || [], merged);
      return { ...merged, items, subtotal, taxAmount, discountAmount, total };
    });
    setCreationMode('workflow');
    setActiveStep(5); // Jump near review for imported invoices
  };

  const handleRouteToExceptionQueue = async () => {
    const apiOrigin = API_BASE_URL.replace(/\/graphql\/?$/, '');
    try {
      const response = await fetch(`${apiOrigin}/api/v1/exceptions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentName: extractedFile?.name || null,
          reason: 'Routed from Import Invoice review — extraction needs manual correction.',
          extractedData,
        }),
      });
      if (!response.ok) {
        throw new Error(`Exception queue request failed (${response.status})`);
      }
      toast({
        title: 'Queued for exception review',
        description: 'This invoice was saved for ops review. Please upload a clearer invoice, or wait for it to be corrected.',
        variant: 'destructive',
      });
    } catch (error) {
      toast({
        title: "Couldn't reach the exception queue",
        description: 'This invoice was NOT saved for review — please try again or notify ops directly.',
        variant: 'destructive',
      });
      return; // keep the extracted data on screen so nothing is silently lost
    }
    setExtractedData(null);
    setExtractedFile(null);
    setCreationMode('import');
  };

  const ecgcStatus = (() => {
    const restrictedCountries = ['iran', 'north korea', 'syria', 'cuba', 'belarus', 'russia'];
    const buyerCountry = (formData.customerCountry || '').toLowerCase();
    const hasExportControl = formData.items.some(item => (item.exportControlFlag || '').toLowerCase().includes('restrict'));
    const missingBuyer = !formData.CustomerName || !formData.customerCountry;
    const noValue = !formData.total || formData.total <= 0;
    const noItems = formData.items.length === 0;

    if (!insuranceSelections.ecgc.applied) {
      return { tone: 'muted', icon: 'CheckCircle', label: 'Not applied', detail: 'Apply ECGC to run checks.' } as const;
    }

    if (noItems) {
      return { tone: 'warning', icon: 'AlertCircle', label: 'Eligibility pending', detail: 'Add at least one item to evaluate.' } as const;
    }

    if (missingBuyer || noValue) {
      return { tone: 'warning', icon: 'AlertCircle', label: 'Eligibility pending', detail: 'Add buyer country and invoice value to check eligibility.' } as const;
    }

    if (formData.total > 500000) {
      return { tone: 'danger', icon: 'XCircle', label: 'Not eligible', detail: 'Invoice value exceeds the covered limit (>$500k).' } as const;
    }

    if (hasExportControl) {
      return { tone: 'danger', icon: 'AlertCircle', label: 'Not eligible', detail: 'Export-controlled item flagged.' } as const;
    }

    if (restrictedCountries.includes(buyerCountry)) {
      return { tone: 'warning', icon: 'AlertCircle', label: 'Manual review', detail: 'Buyer country requires manual ECGC review.' } as const;
    }

    return { tone: 'success', icon: 'CheckCircle', label: 'Eligible', detail: 'Coverage can be applied; documents will generate later.' } as const;
  })();

  const destinationCountry = formData.deliveryCountry || formData.customerCountry || '';

  const countryRisk = (() => {
    const highRisk = ['russia', 'iran', 'north korea', 'syria'];
    const mediumRisk = ['brazil', 'argentina', 'turkey', 'mexico'];
    const code = destinationCountry.toLowerCase();

    if (!destinationCountry) {
      return { level: 'Unknown', emoji: '⚪', restrictions: 'Add destination country', advisory: 'Awaiting details' };
    }
    if (highRisk.includes(code)) {
      return { level: 'High', emoji: '🔴', restrictions: 'Trade restrictions possible', advisory: 'Insurance recommended for this shipment' };
    }
    if (mediumRisk.includes(code) || (formData.total && formData.total > 250000)) {
      return { level: 'Medium', emoji: '🟠', restrictions: 'Monitor for changes', advisory: 'Insurance recommended for this shipment' };
    }
    return { level: 'Low', emoji: '🟢', restrictions: 'None', advisory: 'Insurance optional for this shipment' };
  })();

  const insuranceOptions = [
    {
      id: 'ecgc' as const,
      name: 'Export Credit Insurance (ECGC)',
      summary: 'Buyer default and political risk; best for receivables protection.',
      applicability: 'Applicable to most destinations when buyer and value are known.',
      recommended: ecgcStatus.tone === 'danger' ? 'Manual review' : ecgcStatus.tone === 'success' ? 'Recommended' : 'Optional',
      status: ecgcStatus,
      provider: 'ECGC',
    },
    {
      id: 'cargo' as const,
      name: 'Private Cargo Insurance',
      summary: 'Damage / loss coverage door-to-door.',
      applicability: formData.total > 250000 ? 'Recommended for high-value shipments.' : 'Optional for standard value shipments.',
      recommended: formData.total > 250000 ? 'Recommended' : 'Optional',
      status: { tone: 'success', icon: 'CheckCircle', label: 'Eligible', detail: 'No blocking rules for cargo insurance.' } as const,
      provider: 'Private (AIG / Allianz style)',
    },
  ];

  const steps = [
    {
      label: 'Parties',
      content: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          <CustomerInformation
            formData={formData}
            onChange={handleInputChange}
            onEmailChange={(e) => setFormData(prev => ({ ...prev, CustomerEmail: e.target.value }))}
            onPhoneChange={(value) => setFormData(prev => ({ ...prev, CustomerContactNumber: value }))}
            onEmailValidate={(isValid) => setContactValidity(prev => ({ ...prev, customerEmail: isValid }))}
            onPhoneValidate={(isValid) => setContactValidity(prev => ({ ...prev, customerPhone: isValid }))}
            onAddressAutofill={handleAddressAutofill}
          />
          <VendorInformation
            formData={formData}
            onChange={handleInputChange}
            onEmailChange={(e) => setFormData(prev => ({ ...prev, VendorEmail: e.target.value }))}
            onPhoneChange={(value) => setFormData(prev => ({ ...prev, VendorContactNumber: value }))}
            onEmailValidate={(isValid) => setContactValidity(prev => ({ ...prev, vendorEmail: isValid }))}
            onPhoneValidate={(isValid) => setContactValidity(prev => ({ ...prev, vendorPhone: isValid }))}
            onAddressAutofill={handleAddressAutofill}
          />
        </div>
      )
    },
    {
      label: 'Shipping',
      content: (
        <ShippingInformation
          formData={formData}
          onChange={handleInputChange}
          onAddressAutofill={handleAddressAutofill}
        />
      )
    },
    {
      label: 'Items & Commercials',
      content: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          <ItemsSection
            items={formData.items}
            editingIndex={editingIndex}
            onEdit={(idx) => setEditingIndex(idx)}
            onSave={() => setEditingIndex(null)}
            onCancel={() => setEditingIndex(null)}
            onDelete={(idx) => {
              setEditingIndex(null);
              updateItems(items => items.filter((_, i) => i !== idx));
            }}
            onFieldChange={(idx, field, val) => {
              const numericFields: Array<keyof OrderItem> = ['quantity', 'price', 'unitPrice', 'total', 'subtotal', 'declaredValue'];
              updateItems(items => items.map((item, i) => i === idx ? {
                ...item,
                [field]: numericFields.includes(field) ? Math.max(0, Number(val) || 0) : val,
              } : item));
            }}
            onAddItem={() => {
              const nextItemIndex = formData.items.length;
              updateItems(items => [...items, {
                ...initialNewItem,
                itemno: nextItemIndex + 1,
                countryOfOrigin: formData.pickupCountry || formData.vendorCountry || '',
              }]);
              setEditingIndex(nextItemIndex);
            }}
          />
          <TotalsGrid>
            <Total
              label="Subtotal"
              value={formData.subtotal}
              variant="amount"
              icon={<Icon name="FileText" size={18} />}
              color="#e2e8f0"
              iconColor="#475569"
            />
            <Total
              label="Total Amount"
              value={formData.total}
              variant="amount"
              icon={<Icon name="CreditCard" size={18} />}
              color="#ecfdf3"
              iconColor="#047857"
            />
          </TotalsGrid>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            <div>
              <label htmlFor="poNumber" style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#334155', marginBottom: 6 }}>PO Number</label>
              <input
                id="poNumber"
                type="text"
                name="poNumber"
                value={formData.poNumber || ''}
                onChange={handleInputChange}
                placeholder="Purchase Order #"
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14 }}
              />
            </div>
            <div>
              <label htmlFor="invoiceNumber" style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#334155', marginBottom: 6 }}>Invoice Number</label>
              <input
                id="invoiceNumber"
                type="text"
                name="invoiceNumber"
                value={formData.invoiceNumber || ''}
                onChange={handleInputChange}
                placeholder="Commercial Invoice #"
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14 }}
              />
            </div>
          </div>
        </div>
      )
    },
    {
      label: 'Risk & Insurance',
      content: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <H2 icon={<Icon name="Shield" size={18} color="#f86400" />}>Risk & Insurance (Country-based)</H2>

          <div style={{ display: 'flex', gap: '32px', padding: '16px', background: 'white', border: '1px solid #f1f5f9', borderRadius: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="insuranceRequired"
                checked={formData.insuranceRequired}
                onChange={handleInputChange}
                id="insuranceRequired"
                style={{ width: '18px', height: '18px', accentColor: '#f86400' }}
              />
              <label htmlFor="insuranceRequired" style={{ fontSize: '14px', fontWeight: 500, color: '#334155', cursor: 'pointer' }}>Insurance Required</label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="customsDocumentsRequired"
                checked={formData.customsDocumentsRequired}
                onChange={handleInputChange}
                id="customsDocs"
                style={{ width: '18px', height: '18px', accentColor: '#f86400' }}
              />
              <label htmlFor="customsDocs" style={{ fontSize: '14px', fontWeight: 500, color: '#334155', cursor: 'pointer' }}>Customs Docs Required</label>
            </div>
          </div>

          {/* Country risk overview */}
          <div style={{ background: '#f9fafb', border: '1px solid #e2e8f0', borderRadius: 16, padding: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: '#64748b' }}>Destination</div>
              <div style={{ fontWeight: 600 }}>{destinationCountry || 'Add destination country'}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#64748b' }}>Risk Level</div>
              <div style={{ fontWeight: 700, color: '#111827' }}>{countryRisk.emoji} {countryRisk.level}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#64748b' }}>Trade Restrictions</div>
              <div style={{ fontWeight: 600 }}>{countryRisk.restrictions}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#64748b' }}>Insurance Advisory</div>
              <div style={{ fontWeight: 600 }}>{countryRisk.advisory}</div>
            </div>
          </div>

          {/* Insurance options */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontWeight: 700, color: '#0f172a' }}>Insurance Options (rule-driven)</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
              {insuranceOptions.map(option => {
                const applied = insuranceSelections[option.id].applied;
                const badgeColor = option.recommended === 'Recommended' ? '#f59e0b' : option.recommended === 'Required' ? '#ef4444' : '#e5e7eb';
                const badgeTextColor = option.recommended === 'Recommended' ? '#92400e' : option.recommended === 'Required' ? '#991b1b' : '#4b5563';

                return (
                  <div key={option.id} style={{ border: '1px solid #e2e8f0', borderRadius: 14, padding: 16, background: '#ffffff', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontWeight: 700 }}>{option.name}</div>
                      <span style={{ background: badgeColor, color: badgeTextColor, padding: '4px 10px', borderRadius: 999, fontSize: 12 }}>{option.recommended}</span>
                    </div>
                    <div style={{ color: '#4b5563', fontSize: 13 }}>{option.summary}</div>
                    <div style={{ color: '#6b7280', fontSize: 12 }}>{option.applicability}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                      <Icon name={option.status.icon} size={14} color={option.status.tone === 'success' ? '#16a34a' : option.status.tone === 'danger' ? '#dc2626' : '#f59e0b'} />
                      <span style={{ fontWeight: 600 }}>{option.status.label}</span>
                    </div>
                    {!applied && (
                      <Button
                        variant={option.status.tone === 'danger' ? 'outline' : 'default'}
                        size="sm"
                        disabled={option.status.tone === 'danger'}
                        onClick={() => {
                          setInsuranceSelections(prev => ({
                            ...prev,
                            [option.id]: { ...prev[option.id], applied: true },
                          }));
                        }}
                      >
                        Apply Insurance
                      </Button>
                    )}

                    {applied && (
                      <div style={{ background: '#f9fafb', borderRadius: 12, padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>Coverage type</div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <Button
                            variant={insuranceSelections[option.id].coverageType === 'shipment' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setInsuranceSelections(prev => ({
                              ...prev,
                              [option.id]: { ...prev[option.id], coverageType: 'shipment' },
                            }))}
                          >
                            Shipment-based
                          </Button>
                          <Button
                            variant={insuranceSelections[option.id].coverageType === 'buyer' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setInsuranceSelections(prev => ({
                              ...prev,
                              [option.id]: { ...prev[option.id], coverageType: 'buyer' },
                            }))}
                          >
                            Buyer-based
                          </Button>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
                          <div>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>Estimated Premium</div>
                            <div style={{ fontWeight: 700 }}>${formData.total ? formData.total.toLocaleString() : '0'}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>Provider</div>
                            <div style={{ fontWeight: 700 }}>{option.provider}</div>
                          </div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <label style={{ fontSize: 12, color: '#6b7280' }}>Notes (optional)</label>
                          <textarea
                            style={{ width: '100%', minHeight: 64, padding: 10, borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13 }}
                            value={insuranceSelections[option.id].notes}
                            onChange={(e) => setInsuranceSelections(prev => ({
                              ...prev,
                              [option.id]: { ...prev[option.id], notes: e.target.value },
                            }))}
                          />
                        </div>

                        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
                          <Button
                            variant="default"
                            size="sm"
                            disabled
                            style={{ background: '#ecfdf3', color: '#047857', border: 'none', cursor: 'default' }}
                          >
                            <Icon name="CheckCircle" size={14} style={{ marginRight: '6px' }} />
                            Applied
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setInsuranceSelections(prev => ({
                              ...prev,
                              [option.id]: { applied: false, coverageType: 'shipment', notes: '' },
                            }))}
                          >
                            Remove
                          </Button>
                        </div>

                        {option.id === 'ecgc' && (
                          <div style={{ fontSize: 12, color: '#6b7280' }}>{ecgcStatus.detail}</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )
    },
    {
      label: 'Payment Terms',
      content: (
        <DeliverySchedule
          formData={formData}
          onChange={handleInputChange}
          onRemittanceTrancheChange={handleRemittanceTrancheChange}
          onAddRemittanceTranche={handleAddRemittanceTranche}
          onRemoveRemittanceTranche={handleRemoveRemittanceTranche}
          orderTotal={formData.total}
        />
      )
    },
    {
      label: 'Review & Create',
      content: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <H2 icon={<Icon name="CheckCircle" size={20} color="#f86400" />}>Final Summary</H2>
          <ShippingSummary formData={formData} />
          <ReviewExtractedData
            data={formData}
            onConfirm={() => handleSubmit()}
            onEdit={() => setActiveStep(0)}
          />
        </div>
      )
    }
  ];

  const isBlank = (value?: string | null) => !value || value.trim().length === 0;

  const validateCurrentStep = (step: number = activeStep) => {
    if (step === 0) {
      // State is only a meaningful, selectable field for countries we carry a
      // subdivision list for (US/India) — don't force it for e.g. Poland,
      // which has no state list and would otherwise be permanently disabled.
      const countryHasStates = (country: string) => country === 'US' || country === 'IN';
      const missingFields = [
        ['Customer name', formData.CustomerName],
        ['Customer email', formData.CustomerEmail],
        ['Customer contact number', formData.CustomerContactNumber],
        ['Customer street address', formData.customerAddr1],
        ['Customer city', formData.customerCity],
        ...(countryHasStates(formData.customerCountry) ? [['Customer state', formData.customerState]] : []),
        ['Customer country', formData.customerCountry],
        ['Customer zip', formData.customerZip],
        ['Vendor name', formData.VendorName],
        ['Vendor email', formData.VendorEmail],
        ['Vendor contact number', formData.VendorContactNumber],
        ['Vendor street address', formData.vendorAddr1],
        ['Vendor city', formData.vendorCity],
        ...(countryHasStates(formData.vendorCountry) ? [['Vendor state', formData.vendorState]] : []),
        ['Vendor country', formData.vendorCountry],
        ['Vendor zip', formData.vendorZip],
      ].filter(([, value]) => isBlank(value as string));

      if (missingFields.length > 0) {
        toast({
          title: 'Missing required fields',
          description: `${missingFields[0][0]} is required before continuing.`,
          variant: 'destructive',
        });
        return false;
      }

      const invalidContacts = [
        ['Customer email', contactValidity.customerEmail],
        ['Customer contact number', contactValidity.customerPhone],
        ['Vendor email', contactValidity.vendorEmail],
        ['Vendor contact number', contactValidity.vendorPhone],
      ].filter(([, isValid]) => !isValid);

      if (invalidContacts.length > 0) {
        toast({
          title: 'Check contact details',
          description: `${invalidContacts[0][0]} doesn't look valid. Please correct it before continuing.`,
          variant: 'destructive',
        });
        return false;
      }
    }

    if (step === 1) {
      const pickupAddr1 = formData.vendorSameAsPickup ? formData.vendorAddr1 : formData.pickupAddr1;
      const pickupCity = formData.vendorSameAsPickup ? formData.vendorCity : formData.pickupCity;
      const pickupState = formData.vendorSameAsPickup ? formData.vendorState : formData.pickupState;
      const pickupCountry = formData.vendorSameAsPickup ? formData.vendorCountry : formData.pickupCountry;
      const pickupZip = formData.vendorSameAsPickup ? formData.vendorZip : formData.pickupZip;

      const deliveryAddr1 = formData.deliverySameAsCustomer ? formData.customerAddr1 : formData.deliveryAddr1;
      const deliveryCity = formData.deliverySameAsCustomer ? formData.customerCity : formData.deliveryCity;
      const deliveryState = formData.deliverySameAsCustomer ? formData.customerState : formData.deliveryState;
      const deliveryCountry = formData.deliverySameAsCustomer ? formData.customerCountry : formData.deliveryCountry;
      const deliveryZip = formData.deliverySameAsCustomer ? formData.customerZip : formData.deliveryZip;

      const countryHasStates = (country: string) => country === 'US' || country === 'IN';
      const shippingMissing = [
        ['Pickup street address', pickupAddr1],
        ['Pickup city', pickupCity],
        ...(countryHasStates(pickupCountry) ? [['Pickup state', pickupState]] : []),
        ['Pickup country', pickupCountry],
        ['Pickup zip', pickupZip],
        ['Delivery street address', deliveryAddr1],
        ['Delivery city', deliveryCity],
        ...(countryHasStates(deliveryCountry) ? [['Delivery state', deliveryState]] : []),
        ['Delivery country', deliveryCountry],
        ['Delivery zip', deliveryZip],
      ].filter(([, value]) => isBlank(value as string));

      if (shippingMissing.length > 0) {
        toast({
          title: 'Shipping details incomplete',
          description: `${shippingMissing[0][0]} is required before continuing.`,
          variant: 'destructive',
        });
        return false;
      }
    }

    if (step === 2) {
      if (formData.items.length === 0) {
        toast({
          title: 'No items added',
          description: 'Add at least one item before continuing.',
          variant: 'destructive',
        });
        return false;
      }

      const invalidItem = formData.items.find(item =>
        isBlank(item.productName) || (item.quantity || 0) <= 0 || (item.unitPrice || 0) <= 0,
      );

      if (invalidItem) {
        toast({
          title: 'Item details incomplete',
          description: 'Each item needs product name, quantity, and unit price.',
          variant: 'destructive',
        });
        return false;
      }
    }

    if (step === 4) {
      if (formData.remittanceTranches.length === 0) {
        toast({
          title: 'Remittance schedule required',
          description: 'Add at least one remittance tranche before review.',
          variant: 'destructive',
        });
        return false;
      }

      const invalidTranche = formData.remittanceTranches.find(
        tranche => tranche.percentage <= 0 || isBlank(tranche.dueDate) || isBlank(tranche.milestone),
      );
      if (invalidTranche) {
        toast({
          title: 'Complete remittance schedule',
          description: 'Each remittance row needs percentage, due date, and milestone.',
          variant: 'destructive',
        });
        return false;
      }

      // Validate sequential dates
      for (let i = 1; i < formData.remittanceTranches.length; i++) {
        const prevDate = new Date(formData.remittanceTranches[i - 1].dueDate);
        const currentDate = new Date(formData.remittanceTranches[i].dueDate);
        if (currentDate <= prevDate) {
          toast({
            title: 'Invalid tranche dates',
            description: `Tranche ${i + 1} date must be after Tranche ${i} date.`,
            variant: 'destructive',
          });
          return false;
        }
      }

      const totalPercentage = formData.remittanceTranches.reduce((sum, tranche) => sum + tranche.percentage, 0);
      if (Math.abs(totalPercentage - 100) > 0.01) {
        toast({
          title: 'Invalid remittance percentages',
          description: `Remittance percentages must total 100% (currently ${totalPercentage.toFixed(2)}%).`,
          variant: 'destructive',
        });
        return false;
      }
    }

    return true;
  };

  // Imported invoices jump straight to the Final Summary step (see
  // handleConfirmImport), skipping the per-step validation in
  // validateCurrentStep/handleStepNext entirely — so a field an invoice never
  // had (e.g. customer email, which most freight/logistics invoices don't
  // print at all) can reach submission blank. Re-check every earlier step
  // before submitting and jump back to the first incomplete one, instead of
  // letting the backend reject it with an error that names a field on a
  // screen the user isn't looking at.
  const validateAllSteps = (): number | null => {
    for (const step of [0, 1, 2, 4]) {
      if (!validateCurrentStep(step)) {
        return step;
      }
    }
    return null;
  };

  const handleStepNext = () => {
    if (!validateCurrentStep()) {
      return;
    }

    setActiveStep(prev => Math.min(prev + 1, steps.length - 1));
  };

  const handleSubmit = async () => {
    const firstInvalidStep = validateAllSteps();
    if (firstInvalidStep !== null) {
      setActiveStep(firstInvalidStep);
      return;
    }

    const pickupCity = formData.vendorSameAsPickup ? formData.vendorCity : formData.pickupCity;
    const pickupState = formData.vendorSameAsPickup ? formData.vendorState : formData.pickupState;
    const pickupCountry = formData.vendorSameAsPickup ? formData.vendorCountry : formData.pickupCountry;
    const pickupZip = formData.vendorSameAsPickup ? formData.vendorZip : formData.pickupZip;

    const deliveryCity = formData.deliverySameAsCustomer ? formData.customerCity : formData.deliveryCity;
    const deliveryState = formData.deliverySameAsCustomer ? formData.customerState : formData.deliveryState;
    const deliveryCountry = formData.deliverySameAsCustomer ? formData.customerCountry : formData.deliveryCountry;
    const deliveryZip = formData.deliverySameAsCustomer ? formData.customerZip : formData.deliveryZip;

    // Preparing mutation input
    const input = {
      customer: {
        firstName: formData.CustomerName.split(' ').slice(0, -1).join(' ') || formData.CustomerName,
        lastName: formData.CustomerName.split(' ').slice(-1).join(' ') || '',
        email: formData.CustomerEmail,
        phone: formData.CustomerContactNumber,
        streetAddress: formData.customerAddr1,
        addressLine2: formData.customerAddr2,
        city: formData.customerCity,
        state: formData.customerState,
        country: formData.customerCountry,
        postalCode: formData.customerZip,
      },
      vendor: {
        name: formData.VendorName,
        email: formData.VendorEmail,
        phoneNumber: formData.VendorContactNumber,
        country: formData.vendorCountry,
      },
      items: formData.items.map((item) => ({
        productId: item.productId ? Number(item.productId) : undefined,
        productName: item.productName,
        quantity: Math.max(0, Number(item.quantity) || 0),
        unitPrice: item.unitPrice,
        itemDetails: item.details,
        unit: item.unit,
        hsCode: item.hsCode,
        category: item.category,
        declaredValue: item.declaredValue,
        countryOfOrigin: item.countryOfOrigin,
        exportControlFlag: item.exportControlFlag,
        ecgcEligibility: item.ecgcEligibility,
      })),
      shipment: {
        carrier: undefined,
        packageType: formData.packageType,
        weight: formData.weight ? Number(formData.weight) : undefined,
        dimensions: formData.dimensions,
        origin: `${pickupCity}, ${pickupState}, ${pickupCountry}`,
        destination: `${deliveryCity}, ${deliveryState}, ${deliveryCountry}`,
        estimatedArrivalDate: formData.deliveryDate || undefined,
        pickupCity,
        pickupState,
        pickupCountry,
        pickupPostalCode: pickupZip,
        deliveryCity,
        deliveryState,
        deliveryCountry,
        deliveryPostalCode: deliveryZip,
      },
      currency: formData.currency,
      incoterms: formData.incoterms,
      salesPerson: formData.SalesPerson,
      poNumber: formData.poNumber,
      paymentTerms: buildRemittanceSummary(formData.remittanceTranches),
      remittanceTranches: formData.remittanceTranches.map(tranche => ({
        percentage: tranche.percentage,
        dueDate: tranche.dueDate,
        milestone: tranche.milestone,
      })),
      subtotal: formData.subtotal,
      taxPercent: formData.taxPercent,
      taxAmount: formData.taxAmount,
      discountPercentage: formData.discountPercent,
      discountAmount: formData.discountAmount,
      finalAmount: formData.total,
      orderAmount: formData.subtotal,
      priorityLevel: formData.priorityLevel,
      specialInstructions: formData.specialInstructions,
      insuranceRequired: formData.insuranceRequired,
      customsDocsRequired: formData.customsDocumentsRequired,
      destinationCountry: deliveryCountry || formData.customerCountry,
    };

    try {
      const { data, errors } = await createOrder({ variables: { input } });
      if (data?.createOrder) {
        toast({
          title: '✅ Order Created Successfully!',
          description: `Order ${data.createOrder.orderNo || data.createOrder.id} has been created.`,
        });
        setCreationMode('import');
        setFormData(initialFormData);
        setActiveStep(0);
      } else {
        // errorPolicy is 'all' on this client, so GraphQL errors resolve
        // the promise instead of rejecting it — data is null but nothing throws.
        toast({
          title: '❌ Failed to Create Order',
          description: errors?.[0]?.message || 'Please check your inputs and try again.',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      toast({
        title: '❌ Failed to Create Order',
        description: error?.message || 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <CreateOrderContainer>
      <PageHeaderCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <HeaderContent>
          <IconWrapper>
            <Icon name="PlusCircle" color="#ffffff" size={32} />
          </IconWrapper>
          <TitleGroup>
            <Title>Create Order</Title>
            <Subtitle>Select your preferred method to start a new shipment</Subtitle>
          </TitleGroup>
        </HeaderContent>
      </PageHeaderCard>

      <MethodSelectionGroup>
        <Button
          variant={creationMode === 'import' ? 'default' : 'outline'}
          onClick={() => setCreationMode('import')}
        >
          <Icon name="Upload" size={18} style={{ marginRight: '8px' }} />
          Import Invoice
        </Button>
        <Button
          variant={creationMode === 'bulk' ? 'default' : 'outline'}
          onClick={() => setCreationMode('bulk')}
        >
          <Icon name="Clipboard" size={18} style={{ marginRight: '8px' }} />
          Bulk Upload
        </Button>
        <Button
          variant={creationMode === 'workflow' ? 'default' : 'outline'}
          onClick={() => {
            setCreationMode('workflow');
            setActiveStep(0);
          }}
        >
          <Icon name="FileText" size={18} style={{ marginRight: '8px' }} />
          Manual Order
        </Button>
        <Button
          variant={creationMode === 'erp' ? 'default' : 'outline'}
          onClick={() => setCreationMode('erp')}
        >
          <Icon name="Globe" size={18} style={{ marginRight: '8px' }} />
          ERP Integration
        </Button>
      </MethodSelectionGroup>

      <div style={{ marginTop: '12px', color: '#64748b', fontSize: '13px' }}>
        {creationMode === 'import' && 'Import is the default path for automation — upload a PO or Proforma Invoice and we\'ll extract the order details.'}
        {creationMode === 'bulk' && 'Upload a multi-row CSV/XLSX manifest to add many line items at once.'}
        {creationMode === 'workflow' && 'Use manual order creation only when the buyer or supplier does not have a source invoice.'}
        {creationMode === 'erp' && 'Connect an enterprise resource system (SAP, Oracle, NetSuite) to push order data automatically.'}
      </div>

      <FormCard
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
      >
        {creationMode === 'import' && !extractedData && (
          <ImportInvoice onDataExtracted={handleDataExtracted} />
        )}

        {creationMode === 'import' && extractedData && (
          <ImportInvoiceReview
            data={extractedData}
            file={extractedFile}
            onConfirm={handleConfirmImport}
            onEdit={handleRouteToExceptionQueue}
            secondaryActionLabel="Route to Exception Queue"
          />
        )}

        {creationMode === 'bulk' && (
          <BulkUpload
            onItemsConfirmed={(items) => {
              updateItems(() => items);
              setCreationMode('workflow');
              setActiveStep(2);
            }}
          />
        )}

        {creationMode === 'workflow' && (
          <MultiStepper
            steps={steps}
            activeStep={activeStep}
            onStepChange={setActiveStep}
            onNext={handleStepNext}
            variant="progress"
            activeColor="#f86400"
            showNavigation={activeStep < steps.length - 1}
            nextLabel={activeStep === steps.length - 2 ? 'Review Order' : 'Next Step'}
          />
        )}

        {creationMode === 'erp' && <ErpIntegration />}
      </FormCard>
    </CreateOrderContainer>
  );
};